import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import { StarRating } from "@/components/StarRating";
import { resolveImg } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { FavoriteButton } from "@/components/FavoriteButton";
import { VerifiedBadge } from "@/components/VerifiedBadge";

const TIER_LABEL = { econ: "Economico", medio: "Medio", premium: "Premium" };

export function EditorCard({ editor, index = 0 }) {
    const covers = useMemo(() => (editor.books_edited || []).slice(0, 3), [editor.books_edited]);
    return (
        <Link
            to={`/editors/${editor.editor_id}`}
            data-testid={`editor-card-${editor.editor_id}`}
            className="group block bg-card rounded-lg border border-border overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 opacity-0 animate-fade-up"
            style={{ animationDelay: `${index * 70}ms` }}
        >
            <div className="relative h-56 overflow-hidden bg-secondary">
                {editor.photo_url && <img src={resolveImg(editor.photo_url)} alt={editor.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                <div className="absolute top-3 left-3">
                    <FavoriteButton editorId={editor.editor_id} />
                </div>
                <div className="absolute top-3 right-3">
                    <Badge className="bg-background/90 text-foreground hover:bg-background/90 font-mono-metric text-xs backdrop-blur">
                        da €{editor.starting_price}/cartella
                    </Badge>
                </div>
            </div>
            <div className="p-6">
                <div className="flex items-start justify-between gap-2">
                    <div>
                        <h3 className="font-serif text-2xl font-semibold leading-tight flex items-center gap-1.5">{editor.name}{editor.verified && <VerifiedBadge size={18} />}</h3>
                        <p className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                            <MapPin size={14} /> {editor.location}, {editor.region}
                        </p>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-sage/15 text-sage font-medium shrink-0">
                        {TIER_LABEL[editor.price_tier] || editor.price_tier}
                    </span>
                </div>

                <div className="flex flex-wrap gap-1.5 mt-3">
                    {(editor.genres || []).slice(0, 3).map((g) => (
                        <span key={g} className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">{g}</span>
                    ))}
                </div>

                <div className="flex items-center gap-2 mt-4">
                    <StarRating rating={editor.rating} size={15} />
                    <span className="text-sm font-medium">{editor.rating || "—"}</span>
                    <span className="text-xs text-muted-foreground">
                        · {editor.positive_reviews_count} recensioni positive
                    </span>
                </div>

                {covers.length > 0 && (
                    <div className="flex gap-2 mt-4">
                        {covers.map((b, i) => (
                            <div key={`${b.title}-${i}`} className="h-16 w-11 rounded overflow-hidden bg-secondary shadow-sm">
                                <img src={resolveImg(b.cover_url)} alt={b.title} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Link>
    );
}
